import { Content } from '@elizaos/core';

export interface HistoricalPriceData {
  timestamp: string;
  price: number;
  priceUSD: number;
  volume?: number;
  volumeUSD?: number;
  marketCap?: number;
  marketCapUSD?: number;
  high?: number;
  low?: number;
  open?: number;
  close?: number;
}

export interface TokenMarketData {
  symbol: string;
  name: string;
  address: string;
  network: string;
  currentPrice: number;
  currentPriceUSD: number;
  marketCap?: number;
  marketCapUSD?: number;
  volume24h?: number;
  volume24hUSD?: number;
  priceChange24h?: number;
  priceChangePercent24h?: number;
  volumeChange24h?: number;
  volumeChangePercent24h?: number;
  high24h?: number;
  low24h?: number;
  circulatingSupply?: number;
  totalSupply?: number;
  maxSupply?: number;
  fullyDilutedValuation?: number;
  liquidityScore?: number;
  volatility?: number;
  beta?: number;
  lastUpdated?: string;
}

export interface PriceChartData {
  symbol: string;
  network: string;
  timeframe: string;
  interval: string;
  currency: string;
  data: HistoricalPriceData[];
  indicators?: TechnicalIndicators;
}

export interface TechnicalIndicators {
  sma20?: number; // Simple Moving Average (20 periods)
  ema20?: number; // Exponential Moving Average (20 periods)
  sma?: number[]; // Simple Moving Average array
  ema?: number[]; // Exponential Moving Average array
  rsi?: number; // Relative Strength Index - current value
  macd?: {
    line: number;
    signal: number;
    histogram: number;
  };
  bollinger?: {
    upper: number;
    middle: number;
    lower: number;
  };
  volume?: number;
  volatility?: number;
}

export interface VolumeData {
  timestamp: string;
  volume: number;
  volumeUSD: number;
  transactionCount?: number;
  uniqueUsers?: number;
  averageTransactionSize?: number;
  symbol?: string;
  name?: string;
  address?: string;
  currentPrice?: number;
  priceChange24h?: number;
}

export interface LiquidityData {
  pool: string;
  protocol: string;
  network: string;
  tokenA: string;
  tokenB: string;
  liquidityUSD: number;
  volume24h: number;
  volume24hUSD: number;
  fees24h?: number;
  apr?: number;
  apy?: number;
  impermanentLoss?: number;
  reserves?: {
    tokenA: number;
    tokenB: number;
  };
}

export interface TokenHolderData {
  address: string;
  balance: number;
  balanceUSD: number;
  percentage: number;
  isContract?: boolean;
  label?: string;
  tags?: string[];
  firstSeen?: string;
  lastActive?: string;
  transactionCount?: number;
}

export interface TokenDistribution {
  totalHolders: number;
  concentration: {
    top10: number;
    top50: number;
    top100: number;
  };
  holderTiers: {
    whales: number; // >1M USD
    dolphins: number; // 100K-1M USD
    fish: number; // 10K-100K USD
    shrimp: number; // <10K USD
  };
  holdingPeriods: {
    short: number; // <1 month
    medium: number; // 1-6 months
    long: number; // >6 months
  };
  topHolders: TokenHolderData[];
}

export interface PortfolioPerformance {
  address: string;
  timeframe: TimeFrame;
  startValue: number;
  endValue: number;
  absoluteReturn: number;
  percentageReturn: number;
  realizedPnL: number;
  unrealizedPnL: number;
  totalFees: number;
  gasSpent: number;
  transactionCount: number;
  bestPerformingAsset?: {
    symbol: string;
    return: number;
  };
  worstPerformingAsset?: {
    symbol: string;
    return: number;
  };
  assetAllocation: {
    symbol: string;
    percentage: number;
    value: number;
  }[];
  riskMetrics?: {
    sharpeRatio: number;
    volatility: number;
    maxDrawdown: number;
    beta: number;
  };
}

export interface PortfolioPerformanceData {
  currentValue: number;
  initialValue: number;
  returns?: {
    absolute: number;
    percentage: number;
    annualized: number;
    realizedGains?: number;
    unrealizedGains?: number;
  };
  allocation?: {
    symbol: string;
    name: string;
    value: number;
    percentage: number;
    change24h?: number;
    priceChange24h?: number;
  }[];
  risk?: {
    volatility?: number;
    sharpeRatio?: number;
    maxDrawdown?: number;
    beta?: number;
    valueAtRisk?: number;
  };
  benchmark?: {
    return: number;
    alpha: number;
    beta: number;
    trackingError: number;
    correlation?: number;
  };
}

export interface MarketMetrics {
  totalMarketCap: number;
  totalVolume24h: number;
  defiTVL: number;
  btcDominance: number;
  ethDominance: number;
  activeTokens: number;
  topGainers: TokenMarketData[];
  topLosers: TokenMarketData[];
  trending: TokenMarketData[];
  newListings: TokenMarketData[];
  marketSentiment?: 'extremely_fearful' | 'fearful' | 'neutral' | 'greedy' | 'extremely_greedy';
  fearGreedIndex?: number;
}

export enum TimeFrame {
  ONE_HOUR = '1h',
  ONE_DAY = '1d',
  ONE_WEEK = '1w',
  ONE_MONTH = '1m',
  THREE_MONTHS = '3m',
  SIX_MONTHS = '6m',
  ONE_YEAR = '1y',
  ALL_TIME = 'all',
}

export enum DataInterval {
  MINUTE = '1m',
  FIVE_MINUTES = '5m',
  FIFTEEN_MINUTES = '15m',
  THIRTY_MINUTES = '30m',
  ONE_HOUR = '1h',
  FOUR_HOURS = '4h',
  ONE_DAY = '1d',
  ONE_WEEK = '1w',
  ONE_MONTH = '1M',
}

export interface GetHistoricalPriceContent extends Content {
  symbol: string;
  network?: string;
  timeframe: TimeFrame;
  interval?: DataInterval;
  from?: string;
  to?: string;
  includeVolume?: boolean | string;
  includeMarketCap?: boolean | string;
  includeIndicators?: boolean | string;
  currency?: string;
}

export interface GetMarketDataContent extends Content {
  symbols?: string[];
  network?: string;
  includeMetrics?: boolean;
  includeHolders?: boolean;
  includePerformance?: boolean;
  timeframe?: TimeFrame;
  limit?: number;
  offset?: number;
}

export interface GetTokenHoldersContent extends Content {
  symbol: string;
  network?: string;
  limit?: number;
  offset?: number;
  includeDistribution?: boolean;
  includeAnalytics?: boolean;
  minBalance?: number;
}

export interface GetPortfolioPerformanceContent extends Content {
  address: string;
  timeframe: TimeFrame;
  includeRiskMetrics?: boolean;
  includeAssetBreakdown?: boolean;
  includeFees?: boolean;
  networks?: string[];
  currency?: string;
}

export interface GetLiquidityDataContent extends Content {
  symbol?: string;
  protocol?: string;
  network?: string;
  includeHistorical?: boolean;
  timeframe?: TimeFrame;
  limit?: number;
  offset?: number;
}
